/**
 * Model Defaults Configuration Panel
 * Configure system-wide default models for all categories
 */

import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Loading } from "@/components/common";
import {
  Save,
  RotateCcw,
  AlertCircle,
  MessageSquare,
  Eye,
  Brain,
  ArrowUpDown,
  Mic,
  Lock,
  Image as ImageIcon,
} from "lucide-react";
import {
  useModelDefaultsConfig,
  useUpdateModelDefaultsConfig,
} from "../hooks/useSystemConfig";
import { useAllModelsQuery } from "@/lib/queries/useModelsQuery";
import {
  ModelSelector,
  ModelInfo as SelectorModelInfo,
} from "@/components/ModelSelector";
import { useImageModels } from "@/features/playground/hooks/usePlayground";
import { ModelSelector as ImageModelSelector } from "@/features/playground/components/ModelSelector";
import { useI18n } from "@/i18n";
import type { ModelDefaultsConfig } from "@/types/system";
import type { ModelInfo } from "@/types";

interface ModelFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
  models: ModelInfo[];
}

// Convert global ModelInfo[] to ModelSelector's expected format
const toSelectorModels = (models: ModelInfo[]): SelectorModelInfo[] =>
  models.map((m) => ({
    id: m.id,
    display_name: m.display_name || m.id,
    provider: m.provider || null,
    supports_vision: m.supports_vision || false,
    supports_reasoning: m.supports_reasoning || false,
    max_input_tokens: m.max_input_tokens || null,
    pricing: m.pricing || null,
    estimated_cost: m.estimated_cost || null,
    categories: m.categories || [],
  }));

const ModelField: React.FC<ModelFieldProps> = ({
  label,
  value,
  onChange,
  description,
  models,
}) => (
  <div className="space-y-1.5">
    <ModelSelector
      label={label}
      value={value}
      onChange={(v) => onChange(v || "")}
      models={toSelectorModels(models)}
    />
    {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
  </div>
);

type ModelDefaultsCategory = keyof Omit<
  ModelDefaultsConfig,
  "version" | "updated_by" | "updated_at" | "_meta"
>;

interface ModelParamsProps {
  category: ModelDefaultsCategory;
  formData: ModelDefaultsConfig;
  updateField: (
    category: ModelDefaultsCategory,
    field: string,
    value: any,
  ) => void;
  hasTemperature?: boolean;
  hasTopP?: boolean;
  hasMaxTokens?: boolean;
  hasTopK?: boolean;
}

const ModelParams: React.FC<ModelParamsProps> = ({
  category,
  formData,
  updateField,
  hasTemperature = false,
  hasTopP = false,
  hasMaxTokens = false,
  hasTopK = false,
}) => {
  const data = (formData as any)[category] || {};
  return (
    <div className="flex flex-wrap gap-4 mt-2 pt-2 border-t border-gray-100">
      {hasTemperature && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 w-12">温度</label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="2"
            className="w-20 px-2 py-1 text-sm border rounded"
            value={data.temperature ?? ""}
            onChange={(e) =>
              updateField(
                category,
                "temperature",
                e.target.value ? parseFloat(e.target.value) : undefined,
              )
            }
          />
        </div>
      )}
      {hasTopP && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 w-12">Top P</label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="1"
            className="w-20 px-2 py-1 text-sm border rounded"
            value={data.top_p ?? ""}
            onChange={(e) =>
              updateField(
                category,
                "top_p",
                e.target.value ? parseFloat(e.target.value) : undefined,
              )
            }
          />
        </div>
      )}
      {hasMaxTokens && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 whitespace-nowrap">
            Max Tokens
          </label>
          <input
            type="number"
            step="100"
            min="1"
            max="128000"
            className="w-24 px-2 py-1 text-sm border rounded"
            value={data.max_tokens ?? ""}
            onChange={(e) =>
              updateField(
                category,
                "max_tokens",
                e.target.value ? parseInt(e.target.value) : undefined,
              )
            }
          />
        </div>
      )}
      {hasTopK && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 w-12">Top K</label>
          <input
            type="number"
            step="1"
            min="1"
            max="100"
            className="w-20 px-2 py-1 text-sm border rounded"
            value={data.top_k ?? ""}
            onChange={(e) =>
              updateField(
                category,
                "top_k",
                e.target.value ? parseInt(e.target.value) : undefined,
              )
            }
          />
        </div>
      )}
    </div>
  );
};

interface ModelCategoryCardProps {
  title: string;
  icon: React.ReactNode;
  badge?: string;
  children: React.ReactNode;
}

const ModelCategoryCard: React.FC<ModelCategoryCardProps> = ({
  title,
  icon,
  badge,
  children,
}) => (
  <Card className="p-4">
    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
      {icon}
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {badge && (
        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
          <Lock className="w-3 h-3" />
          {badge}
        </span>
      )}
    </div>
    <div className="grid gap-4 sm:grid-cols-2">{children}</div>
  </Card>
);

export const ModelDefaultsPanel: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.modelDefaults!;

  const { data: config, isLoading, error, refetch } = useModelDefaultsConfig();
  const updateConfig = useUpdateModelDefaultsConfig();
  const { data: allModels, isLoading: isLoadingModels } = useAllModelsQuery();
  const { data: imageModels, isLoading: isLoadingImageModels } =
    useImageModels();

  const [formData, setFormData] = useState<ModelDefaultsConfig | null>(null);

  useEffect(() => {
    if (config) {
      setFormData({
        ...config,
        image: config.image || { model_id: "" },
      });
    }
  }, [config]);

  const handleSave = async () => {
    if (!formData) return;

    // Remove metadata before saving
    const { version, updated_by, updated_at, ...configToSave } = formData;

    try {
      await updateConfig.mutateAsync(configToSave as ModelDefaultsConfig);
      alert(t.saveSuccess);
    } catch (err) {
      alert(`${t.saveFailed}: ${err}`);
    }
  };

  const handleReload = () => {
    refetch();
  };

  const updateField = (
    category: ModelDefaultsCategory,
    field: string,
    value: any,
  ) => {
    if (!formData) return;

    setFormData({
      ...formData,
      [category]: {
        ...formData[category],
        [field]: value,
      },
    });
  };

  if (isLoading || isLoadingModels || isLoadingImageModels) {
    return <Loading message={t.loading} />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>
          {t.loadFailed}: {error.message}
        </span>
      </div>
    );
  }

  if (!formData || !allModels) {
    return null;
  }

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(config);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{t.title}</h2>
          <p className="text-sm text-gray-500 mt-1">{t.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={handleReload}
            disabled={updateConfig.isPending}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t.reload}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateConfig.isPending}
          >
            <Save className="w-4 h-4 mr-2" />
            {updateConfig.isPending ? t.saving : t.save}
          </Button>
        </div>
      </div>

      {/* Section 1: Global Models */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t.globalModelsTitle}
        </h2>
        <p className="text-sm text-gray-500 mb-4">{t.globalModelsDesc}</p>
      </div>

      {/* Memory Model */}
      <ModelCategoryCard
        title={t.memoryModel}
        icon={<Brain className="w-5 h-5 text-green-600" />}
        badge={t.allAgentsShared}
      >
        <ModelField
          label={t.memoryModel}
          value={formData.memory.model_id}
          onChange={(v) => updateField("memory", "model_id", v)}
          description={t.memoryModelDesc}
          models={allModels.memory_models}
        />
        <div />
        <div className="sm:col-span-2">
          <ModelParams
            category="memory"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
            hasMaxTokens
          />
        </div>
      </ModelCategoryCard>

      {/* Embedding & Reranking Models */}
      <ModelCategoryCard
        title={t.embeddingReranking}
        icon={<ArrowUpDown className="w-5 h-5 text-orange-600" />}
        badge={t.allAgentsShared}
      >
        <ModelField
          label={t.embeddingModel}
          value={formData.embedding.model_id}
          onChange={(v) => updateField("embedding", "model_id", v)}
          description={t.embeddingModelDesc}
          models={allModels.embedding_models}
        />
        <div>
          <ModelField
            label={t.rerankingModel}
            value={formData.reranking.model_id}
            onChange={(v) => updateField("reranking", "model_id", v)}
            description={t.rerankingModelDesc}
            models={allModels.reranking_models}
          />
          <ModelParams
            category="reranking"
            formData={formData}
            updateField={updateField}
            hasTopK
          />
        </div>
      </ModelCategoryCard>

      {/* Section 2: Per-Agent Defaults */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t.perAgentDefaultsTitle}
        </h2>
        <p className="text-sm text-gray-500 mb-4">{t.perAgentDefaultsDesc}</p>
      </div>

      {/* Chat Models */}
      <ModelCategoryCard
        title={t.chatModels}
        icon={<MessageSquare className="w-5 h-5 text-blue-600" />}
      >
        <ModelField
          label={t.primaryChatModel}
          value={formData.chat.model_id}
          onChange={(v) => updateField("chat", "model_id", v)}
          description={t.primaryChatModelDesc}
          models={allModels.chat_models}
        />
        <ModelField
          label={t.quickChatModel}
          value={formData.chat.quick_model_id}
          onChange={(v) => updateField("chat", "quick_model_id", v)}
          description={t.quickChatModelDesc}
          models={allModels.chat_models}
        />
        <div className="sm:col-span-2">
          <ModelParams
            category="chat"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
          />
        </div>
      </ModelCategoryCard>

      {/* Vision Models */}
      <ModelCategoryCard
        title={t.visionModels}
        icon={<Eye className="w-5 h-5 text-purple-600" />}
      >
        <ModelField
          label={t.primaryVisionModel}
          value={formData.vision.model_id}
          onChange={(v) => updateField("vision", "model_id", v)}
          description={t.primaryVisionModelDesc}
          models={allModels.vision_models}
        />
        <ModelField
          label={t.quickVisionModel}
          value={formData.vision.quick_model_id}
          onChange={(v) => updateField("vision", "quick_model_id", v)}
          description={t.quickVisionModelDesc}
          models={allModels.vision_models}
        />
        <div className="sm:col-span-2">
          <ModelParams
            category="vision"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
          />
        </div>
      </ModelCategoryCard>

      {/* Auxiliary Models */}
      <ModelCategoryCard
        title={t.auxiliaryModels}
        icon={<Brain className="w-5 h-5 text-green-600" />}
      >
        <div>
          <ModelField
            label={t.classificationModel}
            value={formData.classification.model_id}
            onChange={(v) => updateField("classification", "model_id", v)}
            description={t.classificationModelDesc}
            models={allModels.chat_models}
          />
          <ModelParams
            category="classification"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
            hasMaxTokens
          />
        </div>
        <div>
          <ModelField
            label={t.reasoningModel}
            value={formData.reasoning.model_id}
            onChange={(v) => updateField("reasoning", "model_id", v)}
            description={t.reasoningModelDesc}
            models={allModels.reasoning_models}
          />
          <ModelParams
            category="reasoning"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
          />
        </div>
        <div>
          <ModelField
            label={t.taskModel}
            value={formData.task?.model_id || ""}
            onChange={(v) => updateField("task", "model_id", v)}
            description={t.taskModelDesc}
            models={allModels.chat_models}
          />
          <ModelParams
            category="task"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasMaxTokens
          />
        </div>
      </ModelCategoryCard>

      {/* Speech Models */}
      <ModelCategoryCard
        title={t.speechModels}
        icon={<Mic className="w-5 h-5 text-pink-600" />}
      >
        <ModelField
          label={t.sttModel}
          value={formData.stt.model_id}
          onChange={(v) => updateField("stt", "model_id", v)}
          description={t.sttModelDesc}
          models={allModels.stt_models}
        />
        <ModelField
          label={t.ttsModel}
          value={formData.tts.model_id}
          onChange={(v) => updateField("tts", "model_id", v)}
          description={t.ttsModelDesc}
          models={allModels.tts_models}
        />
      </ModelCategoryCard>

      {/* Image Generation Model */}
      {imageModels && (
        <ModelCategoryCard
          title={t.imageGenerationModels}
          icon={<ImageIcon className="w-5 h-5 text-cyan-600" />}
          badge={t.allAgentsShared}
        >
          <div className="sm:col-span-2">
            <p className="text-xs text-gray-500 mb-3">{t.imageModelDesc}</p>
            <ImageModelSelector
              models={imageModels.models}
              selectedModel={formData.image?.model_id || ""}
              onModelChange={(v) =>
                updateField("image" as ModelDefaultsCategory, "model_id", v)
              }
              compact
            />
          </div>
        </ModelCategoryCard>
      )}

      {/* Metadata Footer */}
      {config?.version !== undefined && (
        <div className="text-sm text-gray-500 border-t border-gray-100 pt-4">
          <p>
            {t.version}: <span className="font-medium">{config.version}</span> |
            {t.lastUpdatedBy}:{" "}
            <span className="font-medium">{config.updated_by || "N/A"}</span> |
            <span className="ml-1">
              {config.updated_at
                ? new Date(config.updated_at).toLocaleString()
                : "N/A"}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};
